import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Merchant } from "src/interfaces/interfaces";
import { APP_VERSION_HEADER, PermissionRole } from "src/constants";
import { apiClient } from "src/lib/apiClient";
import { keys } from "./keys";
import { LinkedLocation, mapLinkedLocation, mapMerchant } from "./mappers";

/**
 * The location domain: the user's list of linked locations, and the full record
 * for one of them.
 *
 * This replaces `merchantsSlice` + `merchantSlice` + the three listener-middleware
 * rules in `src/state/middleware.ts`. Those rules were a hand-rolled invalidation
 * graph — "when a merchant is created or updated, refetch the list" — which is
 * exactly what the `invalidateQueries` calls below express.
 */

/** GET merchant/linked — the locations the signed-in user can administer. */
export const useLocations = (userId: number | undefined) =>
  useQuery<LinkedLocation[]>({
    queryKey: keys.locations.byUser(userId ?? -1),
    enabled: !!userId && userId > 0,
    queryFn: async () => {
      const response = await apiClient.get("merchant/linked", {
        params: { userId },
      });
      return (response.data ?? []).map(mapLinkedLocation);
    },
  });

/**
 * GET merchant/id — one location's full record.
 *
 * The `x-app-version` header is REQUIRED here and must not be moved onto the
 * shared axios instance. The API serves a legacy response shape to clients that
 * omit it, in which `type` is a bare string rather than the full object that
 * `mapMerchant` (and every consumer) expects. Sending it globally would change
 * the shape of unrelated endpoints at the same time.
 */
export const useLocationDetail = (id: number | undefined) =>
  useQuery<Merchant>({
    queryKey: keys.location.detail(id ?? -1),
    enabled: !!id && id > 0,
    queryFn: async () => {
      const response = await apiClient.get("merchant/id", {
        params: { merchantId: id },
        headers: { "x-app-version": APP_VERSION_HEADER },
      });
      return mapMerchant(response.data);
    },
  });

/** Derived from the list, replacing `merchants.computed.isAdminOfSelectedMerchant`. */
export const useIsAdminOfLocation = (
  userId: number | undefined,
  locationId: number | undefined
) => {
  const { data } = useLocations(userId);
  return (
    data?.find((item) => item.id === locationId)?.userPermissionRole ===
    PermissionRole.Admin
  );
};

export const useCreateLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // Keyed so the wizard's footer button can disable itself while the create is
    // in flight via `useIsMutating`, without the form having to report upwards.
    mutationKey: keys.locations.create,
    mutationFn: async (payload: unknown) => {
      const response = await apiClient.post("merchant", payload);
      return mapMerchant(response.data);
    },
    // Ported from the `createMerchant.fulfilled` listener: the new location has
    // to appear in the list before it can be selected.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.locations.all });
    },
  });
};

export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // Note `id` is destructured out rather than deleted from the caller's object
    // — the old thunk did `delete merchant.id`, mutating its own argument.
    mutationFn: async ({ id, ...changes }: { id: number } & Record<string, unknown>) => {
      const response = await apiClient.put(`merchant/${id}`, changes);
      return mapMerchant(response.data);
    },
    onSuccess: (_data, variables) => {
      // The record itself, and the list — a rename has to show up in both.
      queryClient.invalidateQueries({ queryKey: keys.location.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: keys.locations.all });
    },
  });
};

export const useDeleteLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`merchant/${id}`);
      return id;
    },
    onSuccess: (id) => {
      queryClient.removeQueries({ queryKey: keys.location.detail(id) });
      queryClient.invalidateQueries({ queryKey: keys.locations.all });
    },
  });
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ token }: { token: string; locationId: number }) => {
      const response = await apiClient.put(`subscriptions/${token}/cancel`);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // The subscription lives inside the location record.
      queryClient.invalidateQueries({
        queryKey: keys.location.detail(variables.locationId),
      });
    },
  });
};
